import type { PropsWithChildren } from 'react'
import { radius, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Icon, Typography } from '@sunwebgroup/ui-components/uds'

import * as React from 'react'
import { css, styled } from 'styled-components'

import { useActiveStepValue } from './context'

export interface StepProps {
  disabled?: boolean
  completed?: boolean
  link?: {
    href?: string
    target?: string
    title?: string
  }
  value: string
}

const Bullet = styled.span`
  background-color: ${tok('semantics_navigation_item_primary_current_background_color')};
  border-radius: ${radius('primitives_corner_radius_xl')};
  color: ${tok('semantics_navigation_item_primary_current_content_color')};
  display: flex;
  height: 24px;
  width: 24px;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: ${tokPx('primitives_font_size_body_s')};
  font-family: ${tok('primitives_font_family_display')};
  line-height: ${tok('primitives_line_height_tight')};
  font-weight: ${tok('primitives_font_weight_body_extra_bold')};
  position: relative;
  &::before {
    content: '';
  }

  > ${Icon} {
    align-self: center;
    display: none;
  }
`

function Content({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Bullet>
        <Icon glyph="CheckMarkExtraSmall" size={24} />
      </Bullet>
      {children}
    </>
  )
}

const Element = styled.div<{ $active?: boolean, $disabled?: boolean, $completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  counter-increment: stepsnumber;
  flex-basis: 100%;
  flex-grow: 1;
  position: relative;

  > * {
    margin-bottom: ${space('primitives_space_4')};

    &:last-child {
      margin-bottom: initial;
    }
  }

  &:not(:first-child)::before {
    content: '';
    border-top: 2px solid ${tok('semantics_navigation_item_primary_current_background_color')};
    left: calc(-50% + ${space('primitives_space_12')});
    position: absolute;
    top: 10px;
    width: calc(100% - ${space('primitives_space_16')});
  }

  ${({ $active }) => $active && modActive()}
  ${({ $disabled }) => $disabled && modDisabled()}
  ${({ $completed }) => $completed && modCompleted()}
`

const Link = styled.a`
  align-items: center;
  display: flex;
  flex-basis: 100%;
  flex-direction: column;
  flex-grow: 1;
  text-decoration: none;
  color: initial;
  > * {
    margin-bottom: ${space('primitives_space_4')};

    &:last-child {
      margin-bottom: initial;
    }
  }

  &:hover {
    ${Bullet} {
      background-color: ${tok('semantics_navigation_item_primary_current_hover_background_color')};
      color: ${tok('semantics_navigation_item_primary_current_hover_content_color')};
    }

    ${Typography} {
      color: ${tok('semantics_content_color')};
    }
  }
`

function modActive() {
  return css`
  ${Bullet} {
    background-color: ${tok('semantics_navigation_item_primary_current_background_color')};
    color: ${tok('semantics_navigation_item_primary_current_content_color')};
    &::before {
      content: counter(stepsnumber);
      display: block;
    }
  }
`
}

function modDisabled() {
  return css`
  pointer-events: none;

  &:not(:first-child)::before {
    border-top: 2px solid ${tok('primitives_color_neutral_150')};
  }

  ${Bullet} {
    background-color: ${tok('primitives_color_neutral_150')};
    color: ${tok('semantics_content_color')};
    &::before {
      content: counter(stepsnumber);
      display: block;
    }
  }
`
}

function modCompleted() {
  return css`
  ${Bullet} {
    background-color: ${tok('semantics_navigation_item_primary_current_background_color')};
    color: ${tok('semantics_navigation_item_primary_current_content_color')};
  }

  ${Bullet} > ${Icon} {
    display: flex;
  }
`
}

export function Step({ disabled, completed, children, link, value }: PropsWithChildren<StepProps>) {
  const activeStepValue = useActiveStepValue()

  return (
    <Element
      $active={activeStepValue === value}
      $disabled={disabled}
      $completed={completed}
      data-qa="step"
      data-qa-active={activeStepValue === value}
    >
      {link && (
        <Link href={link.href} target={link.target} title={link.title}>
          <Content>{children}</Content>
        </Link>
      )}
      {!link && <Content>{children}</Content>}
    </Element>
  )
}
